<template>
  <div class="mt-5">
    <v-btn icon @click="scrollLeft" class="position-fixed scroll-icons" v-if="showLeft">
      <v-icon>mdi-chevron-left</v-icon>
    </v-btn>

    <div ref="scrollWrapper" class="d-flex overflow-x-auto flex-nowrap w-100 kanban-scroll" @scroll="handleScroll">
      <draggable
        v-model="columns"
        group="columns"
        item-key="id"
        tag="div"
        class="d-flex kanban-draggable"
        :disabled="true"
      >
        <template #item="{ element: column }">
          <v-card class="mr-2" width="300" style="background:transparent;">
            <v-card-title class="status-title">{{ column.name }}</v-card-title>
            <v-divider />

            <!-- Scrollable container -->
            <div class="kanban-tasks" ref="taskWrappers" @scroll.passive="onColumnScroll(column, $event)">
              <draggable
                v-model="column.tasks"
                group="tasks"
                item-key="id"
                tag="div"
                @end="handleTaskMoved"
                :animation="200"
                :ghost-class="'ghost'"
              >
                <template #item="{ element: task }">
                  <v-card class="mx-1 my-2 px-3 py-2" @click="$emit('viewItem', task.id)">
                    <v-card-subtitle class="px-0">{{ task.opportunity_name }}</v-card-subtitle>
                    <v-card-subtitle class="text-caption px-0 mb-1" style="transform:scale(0.8) translateX(-30px) translateY(3px);">
                      <v-icon size="x-small" class="mr-1" v-if="task?.subject?.type=='clients'">mdi-office-building-outline</v-icon>
                      <v-icon size="x-small" class="mr-1" v-if="task?.subject?.type=='leads'">mdi-account-outline</v-icon>
                      {{ task?.subject?.attributes?.legal_name || task?.subject?.attributes?.commercial_name || task?.subject?.attributes?.name }}
                    </v-card-subtitle>
                    <v-chip v-bind="props" link pill size="x-small">
                      <v-avatar class="pa-1" start>
                        <v-img style="border-radius:50%!important;" src="https://ionicframework.com/docs/img/demos/avatar.svg"></v-img>
                      </v-avatar>
                      {{ task?.user?.name + (task?.user?.last_name ? ' ' + task?.user?.last_name : '') }}
                    </v-chip>

                    <v-card-subtitle class="text-caption px-0">{{ $toCurrency(task.value) }}</v-card-subtitle>
                    <v-card-subtitle style="transform:scale(0.8) translateX(-30px);" class="text-caption px-0">{{ task.date }}</v-card-subtitle>

                    <v-progress-circular color="primary" :model-value="task.forecast_percentage*1" :rotate="360" :size="25" :width="2" style="font-size:8px; position:absolute; bottom:10px; right:10px;">
                      <template #default> {{ Math.ceil(task?.forecast_percentage) }}%</template>
                    </v-progress-circular>
                  </v-card>
                </template>
              </draggable>

              <!-- Spinner de carga -->
              <div class="d-flex justify-center my-2" v-if="column.loading">
                <v-progress-circular indeterminate size="24" />
              </div>
            </div>
          </v-card>
        </template>
      </draggable>
    </div>

    <v-btn icon @click="scrollRight" class="position-fixed scroll-icon-right scroll-icons" v-if="showRight">
      <v-icon>mdi-chevron-right</v-icon>
    </v-btn>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, nextTick, watch, computed, PropType } from 'vue';
import draggable from 'vuedraggable';
import { useOpportunitiesStore } from '@/store/opportunities';
import { configRegistry } from '@/lib/configRegistry';
import { useFloatFormStore } from '@/store/floatForm';
import type { FormField } from '@/types/form';
import type { Opportunity } from '@/types/Opportunity';
import { useLayoutStore } from '@/store/layout';

export default defineComponent({
  name: 'KanbanView',
  components: { draggable },
  emits: ['viewItem'],
  props: {
    title: { type: String, required: false },
    moduleName: { type: String, required: true },
    data: { type: Array as PropType<any[]>, required: false },
    config: { type: null, required: false },
    onUpdate: { type: Function as PropType<(id: number, data: any) => Promise<any>>, required: false },
    onRecordUpdate: { type: Function as PropType<(id: number, data: any) => Promise<any>>, required: false },
    statusField: { type: String, required: false },
    client: { type: Object as PropType<any>, required: false },
    clientId: { type: Number, required: false },
  },
  setup(props) {
    const layoutStore = useLayoutStore();
    const filters = computed(() => layoutStore.getLayout(props.moduleName)?.filters || {});

    const scrollWrapper = ref<HTMLElement | null>(null);
    const taskWrappers = ref<any>(null); // sólo para mantener el ref del template
    const showLeft = ref(false);
    const showRight = ref(true);
    const columns = ref<any[]>([]);
    const opportunitiesStore = useOpportunitiesStore();
    const floatFormStore = useFloatFormStore();

    const kanbanConfig = ref({
      confirmForm: false,
      statusField: props.statusField || 'status',
      changeFormFields: [] as FormField[],
    });

    const ITEMS_PER_PAGE = 10;

    const buildPagination = (page: number, filtersObj: Record<string, any>) => {
      const searchParams = new URLSearchParams();
      searchParams.append('itemsPerPage', ITEMS_PER_PAGE.toString());
      searchParams.append('page', page.toString());
      for (const [key, val] of Object.entries(filtersObj)) {
        if (val !== null && val !== '') {
          if (typeof val === 'object' && (val as any).id) {
            searchParams.append(`filter[${key}]`, (val as any).id);
          } else {
            searchParams.append(`filter[${key}]`, String(val));
          }
        }
      }
      return `&${searchParams.toString()}`;
    };

    const loadKanbanConfig = async () => {
      if (!props.moduleName) return;
      const loader = (configRegistry as any)[props.moduleName];
      if (!loader) return;
      const mod = await loader();
      if (mod?.KANBAN) {
        kanbanConfig.value = {
          confirmForm: !!mod.KANBAN.confirmForm,
          statusField: mod.KANBAN.statusField || 'status',
          changeFormFields: mod.KANBAN.changeFormFields || [],
        };
      }
    };

    const loadOpportunities = async () => {
      if (!props.data || props.data.length === 0) return;

      const activeFilters = {
        ...filters.value,
        ...(props.clientId && { subject_id: props.clientId }),
        ...(props.client?.type && { subject: props.client?.type }),
      };

      const loadedColumns = await Promise.all(
        props.data.map(async (status: any) => {
          const pagination = buildPagination(1, { ...activeFilters });
          const opportunities = await opportunitiesStore.fetchOpportunityByStatusName(status.name, pagination);
          return {
            id: status.id,
            name: status.name,
            tasks: opportunities || [],
            page: 1,
            hasMore: (opportunities?.length || 0) === ITEMS_PER_PAGE,
            loading: false,
          };
        })
      );

      columns.value = loadedColumns;
    };

    const onColumnScroll = async (column: any, event: Event) => {
      const el = event.target as HTMLElement;
      if (!el || column.loading || !column.hasMore) return;

      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
      if (!atBottom) return;

      column.loading = true;
      column.page += 1;

      try {
        const activeFilters = filters.value;
        const pagination = buildPagination(column.page, { ...activeFilters, status: column.name });
        const moreTasks = await opportunitiesStore.fetchOpportunityByStatusName(column.name, pagination);
        if (moreTasks && moreTasks.length > 0) {
          column.tasks.push(...moreTasks);
          column.hasMore = moreTasks.length === ITEMS_PER_PAGE;
        } else {
          column.hasMore = false;
        }
      } catch (e) {
        console.error('Error al cargar más tareas:', e);
        column.hasMore = false;
      } finally {
        column.loading = false;
      }
    };

    const handleTaskMoved = async (evt: any) => {
      const movedTask = evt.item?._underlying_vm_;
      if (!movedTask || !props.onRecordUpdate) return;

      const fromContainer = evt.from;
      const toContainer = evt.to;

      const sourceColumn = columns.value.find(col =>
        fromContainer?.closest('.v-card')?.querySelector('.v-card-title')?.textContent?.trim() === col.name
      );

      const destinationColumn = columns.value.find(col =>
        toContainer?.closest('.v-card')?.querySelector('.v-card-title')?.textContent?.trim() === col.name
      );

      if (!destinationColumn || !sourceColumn) return;

      const statusField = kanbanConfig.value.statusField;
      const newStatus = destinationColumn.name;
      const oldStatus = sourceColumn.name;

      if (oldStatus === newStatus) return;

      let payload: Record<string, any> = { [statusField]: newStatus };

      try {
        if (kanbanConfig.value.confirmForm && kanbanConfig.value.changeFormFields.length) {
          const extraData = await floatFormStore.openFormWithPromise({
            component: 'EvidenceForm',
            formTitle: 'Confirmar cambio de estado',
            formWidth: 700,
            props: { fields: kanbanConfig.value.changeFormFields },
          });
          payload = { ...payload, ...extraData };
        }

        await props.onRecordUpdate!(movedTask.id, payload);
      } catch (e) {
        destinationColumn.tasks = destinationColumn.tasks.filter((t: Opportunity) => t.id !== movedTask.id);
        sourceColumn.tasks.splice(evt.oldIndex, 0, movedTask);
      }
    };

    const handleScroll = () => {
      const el = scrollWrapper.value;
      if (!el) return;

      const hasData = columns.value.length > 0;
      showLeft.value = el.scrollLeft > 0;

      if (hasData) {
        showRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth;
      } else {
        showRight.value = true;
      }
    };

    const scrollLeft = () => {
      scrollWrapper.value?.scrollBy({ left: -1500, behavior: 'smooth' });
    };

    const scrollRight = () => {
      scrollWrapper.value?.scrollBy({ left: 1500, behavior: 'smooth' });
    };

    onMounted(async () => {
      await loadKanbanConfig();
      await loadOpportunities();

      nextTick(() => {
        handleScroll();
        window.addEventListener('resize', handleScroll);
        scrollWrapper.value?.addEventListener('wheel', (e: any) => {
          if (e.shiftKey) {
            e.preventDefault();
            scrollWrapper.value!.scrollLeft += e.deltaY;
          }
        });
      });
    });

    watch(() => props.data, loadOpportunities);

    return {
      // refs / state
      scrollWrapper,
      taskWrappers,
      showLeft,
      showRight,
      columns,

      // methods
      handleScroll,
      onColumnScroll,
      handleTaskMoved,
      scrollLeft,
      scrollRight,

      // expose props to template (usado en v-bind="props" en un chip)
      props,
    };
  },
});
</script>

<style scoped>
.kanban-scroll { scroll-behavior: smooth; }
.kanban-draggable { min-height: calc(100vh - 200px); }

.scroll-icons {
  top: 50vh;
  transform: translateY(-50%);
  z-index: 10;
}
.scroll-icon-right { right: 10px; }

.status-title { font-size: 12px; }

.ghost { opacity: 0.4; transform: rotate(3deg); }

.v-card { transition: all 0.3s ease; }

.kanban-tasks {
  max-height: calc(100vh - 250px);
  overflow-y: auto;
}

/*.kanban-scroll::-webkit-scrollbar,*/
.kanban-tasks::-webkit-scrollbar { display: none!important; }
/*.kanban-scroll,*/
.kanban-tasks {
  scrollbar-width: none!important;
  -ms-overflow-style: none!important;
}
</style>
